// shared/image-url.ts — de URL van een verkleinde variant van een upload.
//
// Gedeeld door server (srcset in de artikel-HTML) en client (de miniatuur op
// een kaart), zodat beide kanten dezelfde regel hanteren. De varianten zelf
// komen uit server/services/image-variants.ts: `/uploads/<naam>?w=<breedte>`
// levert een WebP van hoogstens die breedte. Valt de server terug op het
// origineel (onbekende breedte, geen raster), dan is de URL nog steeds geldig
// — alleen zonder winst.

/** De breedtes die de server maakt. Hier gespiegeld (niet geïmporteerd) omdat
 *  de clientbundel server/ niet mag zien; tests/image-url.test.ts bewaakt dat
 *  de twee lijsten gelijk blijven. */
export const IMAGE_WIDTHS = [480, 800, 1600] as const;
export type ImageWidth = (typeof IMAGE_WIDTHS)[number];

const RASTER = /\.(png|jpe?g|webp|gif)$/i;

/** Alleen eigen uploads zonder querystring; externe plaatjes, SVG's en
 *  data-URI's blijven zoals ze zijn. */
export function isVariantable(src: string | null | undefined): src is string {
  if (!src) return false;
  if (!src.startsWith('/uploads/')) return false;
  if (src.includes('?') || src.includes('#')) return false;
  return RASTER.test(src);
}

export function variantUrl(src: string, width: ImageWidth): string {
  return `${src}?w=${width}`;
}

/** `srcset` voor een afbeelding die op het scherm hoogstens `maxCss` px breed
 *  staat. Breedtes groter dan het origineel laten we weg: de server zou toch
 *  hetzelfde bestand teruggeven en de browser zou het als groter beschouwen. */
export function srcsetFor(src: string, originalWidth?: number | null): string {
  const breedtes = IMAGE_WIDTHS.filter((w) => !originalWidth || w < originalWidth);
  if (originalWidth && breedtes.length < IMAGE_WIDTHS.length) {
    // Het origineel zelf als grootste kandidaat, zodat de browser nooit een
    // opgeschaalde variant kiest.
    return [...breedtes.map((w) => `${variantUrl(src, w)} ${w}w`), `${src} ${originalWidth}w`].join(', ');
  }
  return breedtes.map((w) => `${variantUrl(src, w)} ${w}w`).join(', ');
}
